import { BMPrimitives, useBMTheme } from "@traveloka/district-bloom";
import { useCallback, useMemo } from "react";

import { BarSeries } from "../AnalyticsBarChart/AnalyticsBarChart";
import { ViewType } from "../types";
import { JavaCurrencyValue } from "../../../utils/currency";
import { ProductBarChartEntry, ProductBarChartTransaction } from "./types";
import { getBucketKey, rankIndexOf, rankKey, toNumericValue } from "./utils";

export default function useProductBarChartSeries(
  transactions: ProductBarChartTransaction[],
  viewType: ViewType = "MONTHLY"
) {
  const { BMComponents } = useBMTheme();
  const colorPalette = useMemo(
    () => [
      BMPrimitives.BMBrand.primary,
      "#826dbe",
      "#5cc477",
      BMComponents.BMStyleIconInkHighlightRed,
      BMPrimitives.BMBrand.productTrains,
    ],
    [BMComponents]
  );

  const rankCount = useMemo(
    () => transactions.reduce((max, t) => Math.max(max, t.entries.length), 0),
    [transactions]
  );

  const series: BarSeries[] = useMemo(
    () =>
      Array.from({ length: rankCount }, (_, i) => ({
        key: rankKey(i),
        label: `#${i + 1}`,
        color: colorPalette[i % colorPalette.length],
      })),
    [rankCount, colorPalette]
  );

  const nameToColor = useMemo(() => {
    const totalByName = new Map<string, number>();
    transactions.forEach((t) =>
      t.entries.forEach((e) => {
        totalByName.set(
          e.name,
          (totalByName.get(e.name) ?? 0) + toNumericValue(e.value)
        );
      })
    );
    const names = Array.from(totalByName.keys()).toSorted(
      (a, b) => (totalByName.get(b) ?? 0) - (totalByName.get(a) ?? 0)
    );
    const map: Record<string, string> = {};
    names.forEach((name, i) => {
      map[name] = colorPalette[i % colorPalette.length];
    });
    return map;
  }, [transactions, colorPalette]);

  const legendItems: BarSeries[] = useMemo(
    () =>
      Object.entries(nameToColor).map(([name, color]) => ({
        key: name,
        label: name,
        color,
      })),
    [nameToColor]
  );

  const { chartData, sortedEntriesByDate } = useMemo(() => {
    const bucketedValues = new Map<
      string,
      Map<string, JavaCurrencyValue | number>
    >();
    const bucketedLogoUrls = new Map<string, Map<string, string | undefined>>();
    const sortDates = new Map<string, string>();

    transactions.forEach((t) => {
      const date = getBucketKey(t, viewType);
      const values =
        bucketedValues.get(date) ??
        new Map<string, JavaCurrencyValue | number>();
      const logoUrls =
        bucketedLogoUrls.get(date) ?? new Map<string, string | undefined>();

      t.entries.forEach((e) => {
        values.set(e.name, e.value);
        if (e.logoUrl) logoUrls.set(e.name, e.logoUrl);
      });

      bucketedValues.set(date, values);
      bucketedLogoUrls.set(date, logoUrls);

      // multiple transactions can map to the same bucket (e.g. several months in a quarter);
      // keep the earliest sortDate so the bucket sorts by its start, not its last-seen entry.
      const sortDate = t.sortDate ?? `${t.month} 1, ${t.year}`;
      const existingSortDate = sortDates.get(date);
      if (
        !existingSortDate ||
        new Date(sortDate).getTime() < new Date(existingSortDate).getTime()
      ) {
        sortDates.set(date, sortDate);
      }
    });

    const sortedEntriesByDate: Record<string, ProductBarChartEntry[]> = {};

    const rows = Array.from(bucketedValues.entries()).map(([date, values]) => {
      const logoUrls = bucketedLogoUrls.get(date);
      const sortedEntries = Array.from(values.entries())
        .map(([name, value]) => ({
          name,
          value,
          logoUrl: logoUrls?.get(name),
        }))
        .toSorted((a, b) => toNumericValue(b.value) - toNumericValue(a.value));
      sortedEntriesByDate[date] = sortedEntries;

      const row: Record<string, string | number> = { date };
      sortedEntries.forEach((e, i) => {
        row[rankKey(i)] = toNumericValue(e.value);
      });
      return row;
    });

    const chartData = rows.toSorted(
      (a, b) =>
        new Date(sortDates.get(String(a.date)) ?? 0).getTime() -
        new Date(sortDates.get(String(b.date)) ?? 0).getTime()
    );

    return { chartData, sortedEntriesByDate };
  }, [transactions, viewType]);

  const getCellFill = useCallback(
    (dataKey: string, row: Record<string, string | number>) => {
      const product = sortedEntriesByDate[row.date]?.[rankIndexOf(dataKey)];
      return product && nameToColor[product.name];
    },
    [sortedEntriesByDate, nameToColor]
  );

  return {
    series,
    nameToColor,
    legendItems,
    chartData,
    sortedEntriesByDate,
    getCellFill,
  };
}
